<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="documentSeries"
      :headers="tableHeaders"
      :list="documentSeries"
      table-id="documentSeries"
      clickable
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="refreshData"
      @table-item-clicked="onItemClick"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as DocumentSeries)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'vue-router';
import { removeDocumentSeriesByIds, getAllDocumentSeries } from '@/services/api/documents';

import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { type DocumentSeries } from '@/types/DocumentSeries';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });

const { reloadLookups } = useLookupsStore();
const router = useRouter();

const documentSeries = ref<DocumentSeries[]>();
const filterIsActive = ref<boolean | undefined>(undefined);

const tableHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'prefix', text: $t('series.prefix'), type: 'text', active: true },
  { value: 'suffix', text: $t('series.suffix'), type: 'text', active: true },
  {
    value: 'hasAutomaticNumbering',
    text: $t('series.automaticNumber'),
    type: 'check',
    active: true,
  },
  { value: 'sequenceString', text: $t('series.sequence'), type: 'text', active: true },
  { value: 'isCancelingSeries', text: $t('series.cancelingSeries'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

function initializeDocumentSeries(): DocumentSeries {
  return {
    id: uuidv4(),
    code: '',
    name: '',
    isCancelingSeries: false,
    hasAutomaticNumbering: false,
    isFromManager: true,
    isActive: false,
    sequence: 0,
  };
}

onMounted(async () => {
  loadData();
});

async function loadData() {
  const res = await getAllDocumentSeries();
  documentSeries.value = populateDocumentSeriesField(res);
}

function populateDocumentSeriesField(documentSeries: DocumentSeries[]): DocumentSeries[] {
  return documentSeries.map((series) => ({
    ...series,
    sequenceString: series.sequence.toString(),
  }));
}

const toastStore = useToastStore();

async function onDeleteMultiselect(items: DocumentSeries[]) {
  const itemsArray = Array.isArray(items) ? items : [items];
  const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.error({ text: 'Selected items cannot be deleted.' });
    return;
  }

  const idsToDelete = itemsToDelete.map((item) => item.id);
  const res = await removeDocumentSeriesByIds(idsToDelete);
  if (!res && documentSeries.value)
    documentSeries.value = documentSeries.value.filter(
      (series) => !idsToDelete.includes(series.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['documentSeries']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeDocumentSeriesByIds([idToDelete]);
  if (!res && documentSeries.value)
    documentSeries.value = documentSeries.value.filter((series) => series.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['documentSeries']);
}

const series = ref<DocumentSeries>(initializeDocumentSeries());

function onAddNew() {
  series.value = initializeDocumentSeries();
  router.push({ name: 'document-series-details', params: { id: series.value.id } });
}

async function refreshData(data: TableFilterData) {
  await loadData();
  if (data.query && documentSeries.value) {
    documentSeries.value = filterData(documentSeries.value, data.query, ['name', 'code']);
  }

  if (data.column !== undefined && data.order && documentSeries.value) {
    documentSeries.value = sortData(documentSeries.value, data.column, data.order, tableHeaders);
  }

  if (data.isActive !== undefined && documentSeries.value)
    documentSeries.value = filterActive(documentSeries.value, data.isActive);
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DocumentSeries>();
function onDeleteRow(item: DocumentSeries) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onItemClick(item: DocumentSeries) {
  router.push({ name: 'document-series-details', params: { id: item.id } });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
